import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { customAlphabet } from "nanoid";
import { v3apiInstance } from "~utils/axios";
import { tryCatch } from "~utils/try-catch";

import {
	SignUpV3FormValues,
	SignUpV3RemoteFormValues,
} from "~ui-components/components/templates/sign-up-v3";

// Reserved header consumed by `apps/web/pages/api/auth/sign-up-v3.ts`. The
// token is intentionally carried in headers so the multipart body stays
// byte-for-byte unchanged for Rails and the token never reaches the upstream.
export const SIGN_UP_V3_RECAPTCHA_HEADER = "x-recaptcha-token";

export interface SignUpV3MutationValues
	extends SignUpV3FormValues,
		SignUpV3RemoteFormValues {
	recaptchaToken: string;
}

export const useSignUpV3 = () => {
	return useMutation({
		mutationKey: ["signUp"],
		mutationFn: async ({
			recaptchaToken,
			...values
		}: SignUpV3MutationValues) => {
			const companyName = getRandomCompanyName();
			const companySlug = await getCompanySlug(companyName);

			const postForm = new FormData();
			postForm.append("user[first_name]", values.name);
			postForm.append("user[last_name]", getRandomizedChars());
			postForm.append("user[user_name]", companyName);
			postForm.append("user[portal_url]", companySlug);
			postForm.append("user[phone]", "855123456789");
			postForm.append("user[email]", values.email);
			postForm.append("user[password]", values.password);

			return v3apiInstance.post("/clients/create_account.json", postForm, {
				headers: { [SIGN_UP_V3_RECAPTCHA_HEADER]: recaptchaToken },
			});
		},
	});
};

const PREFIXES = [
	"atlas",
	"bay",
	"cape",
	"delta",
	"edge",
	"fjord",
	"grid",
	"hill",
	"isle",
	"jet",
	"key",
	"land",
	"mesa",
	"node",
	"park",
	"quad",
	"ridge",
	"sea",
	"trail",
	"vale",
	"wave",
	"zone",
	"arc",
	"cliff",
	"dune",
	"east",
	"field",
	"gulf",
	"harbor",
	"inlet",
	"knoll",
	"lake",
	"mount",
	"north",
	"ocean",
	"path",
	"range",
	"south",
	"trace",
	"under",
	"view",
	"west",
	"yard",
	"zenith",
	"basin",
	"creek",
	"depth",
	"fleet",
	"grove",
];

function getRandomCompanyName() {
	const prefix = PREFIXES[Math.floor(Math.random() * PREFIXES.length)];
	const suffix = getRandomizedChars();

	return `${prefix} ${suffix}`;
}

function getRandomizedChars() {
	return customAlphabet(
		"0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
		8,
	)();
}

const getCompanySlug = async (value: string) => {
	const [error, data] = await tryCatch(
		v3apiInstance.get<{
			portal_url: string | null;
			status: string;
			users: Record<string, any>[];
		}>(`/account/search?term=${encodeURIComponent(value)}&qc=name`),
	);

	if (error) {
		if (isAxiosError(error)) throw new Error("Something went wrong!");
		throw new Error(error.message);
	}
	if (!data.portal_url) throw new Error("Invalid company name");

	return data.portal_url;
};
